import { useEffect, useId, useState } from 'react';
import { api } from '../../api';
import { horaCurta } from '../../formato';
import { ErroDaApi } from '../../ui';
import './agente.css';

/**
 * A sugestão do agente na conversa — onda 552 (Plano 2 · Comercial, Etapa 1,
 * Tarefa 24; spec R02 §7.5, o copiloto).
 *
 * Uma caixa acima do compositor (P-LST-15: a conversa é a tela de trabalho),
 * com o que o agente sugeriu para a última mensagem do cliente:
 *
 *  · **Enviar** — sai como está, como resposta da pessoa (conta como «sem
 *    correção»);
 *  · **Editar** — o texto vira um campo; «Enviar» manda o que a pessoa
 *    escreveu (mudado, conta como corrigido);
 *  · **Descartar** — some da caixa, e nada sai.
 *
 * O primário da conversa continua sendo o «Enviar» do compositor (P-UI-10):
 * aqui os botões são secundários, como as ações do cabeçalho.
 *
 * O rascunho que as travas reprovaram não aparece: a caixa diz «O agente não
 * sugeriu: <motivo>» (o motivo vem da API pela máscara de valores) e só deixa
 * descartar. A sugestão que pode citar valores (M-10) chega a quem não tem o
 * Comercial só como aviso de que existe.
 *
 * Chega ao vivo: a conversa ouve `comercial.conversa.<id>`, e a 0513 avisa
 * quando a decisão do agente termina em sugestão e quando ela se resolve.
 */

export interface SugestaoDaConversa {
  decisaoId: string;
  tipo: 'sugerida' | 'retida';
  oculta: boolean;
  texto: string | null;
  motivo: string | null;
  em: string;
}

export function SugestaoDoAgente({ sugestao, bloqueio, aoResolver }: {
  sugestao: SugestaoDaConversa;
  /** Por que a pessoa não responde aqui (a conversa é de outra): enviar e editar ficam indisponíveis. */
  bloqueio: string | null;
  aoResolver(r: { aviso: string | null; ok: string }): void;
}) {
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState(sugestao.texto ?? '');
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  const id = useId();

  useEffect(() => {
    setEditando(false); setTexto(sugestao.texto ?? ''); setErro(null);
  }, [sugestao.decisaoId, sugestao.texto]);

  const resolver = async (acao: 'enviar' | 'editar' | 'descartar') => {
    if (ocupado) return;
    setOcupado(true);
    setErro(null);
    try {
      const r = await api<{ aviso: string | null }>(`/comercial/sugestoes/${sugestao.decisaoId}/resolver`, {
        metodo: 'POST',
        corpo: acao === 'editar' ? { acao, texto: texto.trim() } : { acao },
      });
      aoResolver({
        aviso: r?.aviso ?? null,
        ok: acao === 'descartar' ? 'A sugestão do agente foi descartada.' : 'A resposta foi para a fila de envio.',
      });
    } catch (e) {
      setErro(e);
    } finally {
      setOcupado(false);
    }
  };

  const retida = sugestao.tipo === 'retida';
  const podeEnviar = !retida && !sugestao.oculta && !bloqueio;
  const idTexto = `${id}-texto`;
  const idBloqueio = `${id}-bloqueio`;
  return (
    <section className={`sugestao-agente${retida ? ' retida' : ''}`} aria-labelledby={`${id}-titulo`}>
      <p className="sugestao-agente-titulo" id={`${id}-titulo`}>
        {retida ? 'O agente reteve a resposta' : 'Sugestão do agente'}
        <span className="sugestao-agente-hora"> · {horaCurta(sugestao.em)}</span>
      </p>
      {retida ? (
        <p className="sugestao-agente-texto" id={idTexto}>
          {sugestao.oculta
            ? 'O agente não sugeriu: o motivo cita dados do comercial, que só quem cuida do comercial lê.'
            : `O agente não sugeriu: ${sugestao.motivo ?? 'as travas reprovaram a resposta'}.`}
        </p>
      ) : sugestao.oculta ? (
        <p className="sugestao-agente-texto" id={idTexto}>
          O agente sugeriu uma resposta que pode citar valores ou propostas, e ela é de quem cuida do comercial.
          Escreva a sua resposta abaixo, ou passe a conversa a quem cuida do comercial.
        </p>
      ) : editando ? (
        <div className="campo sugestao-agente-campo">
          <textarea value={texto} rows={3} aria-label="Resposta editada" maxLength={10000}
            onChange={(e) => setTexto(e.target.value)} />
        </div>
      ) : (
        <p className="sugestao-agente-texto" id={idTexto}>{sugestao.texto}</p>
      )}
      {bloqueio && <p className="sugestao-agente-aux" id={idBloqueio}>{bloqueio}</p>}
      {erro ? <ErroDaApi erro={erro} /> : null}
      <div className="sugestao-agente-acoes">
        {podeEnviar && (editando ? (
          <>
            <button type="button" className="botao secundario" aria-disabled={ocupado || !texto.trim()}
              onClick={() => { if (texto.trim()) void resolver('editar'); }}>
              {ocupado ? 'Enviando…' : 'Enviar'}
            </button>
            <button type="button" className="botao discreto" onClick={() => { setEditando(false); setTexto(sugestao.texto ?? ''); }}>
              Cancelar a edição
            </button>
          </>
        ) : (
          <>
            <button type="button" className="botao secundario" aria-disabled={ocupado} aria-describedby={idTexto}
              onClick={() => void resolver('enviar')}>
              {ocupado ? 'Enviando…' : 'Enviar'}
            </button>
            <button type="button" className="botao secundario" onClick={() => setEditando(true)}>Editar</button>
          </>
        ))}
        {/* Descartar segue a regra de responder: com a conversa de outra pessoa, não aparece (m-3). */}
        {!editando && !bloqueio && (
          <button type="button" className="botao discreto" aria-disabled={ocupado} onClick={() => void resolver('descartar')}>
            Descartar
          </button>
        )}
      </div>
    </section>
  );
}
